import { PAISES } from '../models/catalogos';

/** Traducciones amigables para los valores crudos (enums en inglés/backend) que llegan del API. */
export const SENIORITY_LABELS: Record<string, string> = {
  JUNIOR: 'Junior',
  MID: 'Nivel medio',
  SENIOR: 'Senior',
  LEAD: 'Líder / Lead',
  NO_APLICA: 'Sin experiencia previa',
};

export const CONTRATO_LABELS: Record<string, string> = {
  tiempo_completo: 'Tiempo completo',
  medio_tiempo: 'Medio tiempo',
  por_proyecto: 'Por proyecto',
  freelance: 'Freelance',
};

export const MODALIDAD_LABELS: Record<string, string> = {
  PRESENCIAL: 'Presencial',
  REMOTO: 'Remoto',
  HIBRIDO: 'Híbrido',
};

/** Mapa de código de país a segmento de URL. Países no listados degradan a su código en minúsculas. */
export const PAIS_SLUG_MAP: Record<string, string> = {
  MX: 'mx',
  CO: 'co',
  CR: 'cr',
  EC: 'ec',
  PE: 'pe',
  CL: 'cl',
};

export function seniorityLabel(valor?: string | null): string {
  if (!valor) return 'No especificado';
  return SENIORITY_LABELS[valor] ?? valor;
}

export function contratoLabel(valor?: string | null): string {
  if (!valor) return 'No especificado';
  return CONTRATO_LABELS[valor] ?? valor;
}

export function modalidadLabel(valor?: string | null): string {
  if (!valor) return 'No especificado';
  return MODALIDAD_LABELS[valor] ?? valor;
}

export function paisFlag(pais?: string | null): string {
  if (!pais || !/^[A-Za-z]{2}$/.test(pais.trim())) return '';
  return String.fromCodePoint(
    ...[...pais.trim().toUpperCase()].map((char) => 127397 + char.charCodeAt(0))
  );
}

/**
 * Códigos de país que las fuentes de scraping pueden incrustar en el valor
 * de ciudad (misma lista que {@code UbicacionUtil} en el backend).
 */
const CODIGOS_PAIS_CIUDAD = [
  'MX', 'CO', 'EC', 'CR', 'PE', 'AR', 'CL', 'PA', 'UY', 'DO',
  'GT', 'SV', 'HN', 'NI', 'PY', 'BO', 'VE',
];

/**
 * Elimina el código de país incrustado por el scraping en el valor de
 * ciudad: como prefijo ("Mx Ciudad de México") o como sufijo tras coma
 * ("Ciudad de México, MX"). Espejo del {@code UbicacionUtil} del backend
 * para que el landing sea robusto aunque el dato ya esté almacenado sucio.
 */
export function limpiarCiudadPrefijoPais(ciudad?: string | null): string {
  if (!ciudad) return '';
  let valor = ciudad.trim();
  for (const codigo of CODIGOS_PAIS_CIUDAD) {
    const prefijo = `${codigo} `;
    if (valor.length > prefijo.length && valor.slice(0, prefijo.length).toUpperCase() === prefijo) {
      valor = valor.slice(prefijo.length).trim();
      break;
    }
  }
  for (const codigo of CODIGOS_PAIS_CIUDAD) {
    const sufijo = `, ${codigo}`;
    if (valor.length > sufijo.length && valor.slice(valor.length - sufijo.length).toUpperCase() === sufijo) {
      valor = valor.slice(0, valor.length - sufijo.length).trim();
      break;
    }
  }
  return valor;
}

export function ubicacionTexto(ciudad?: string | null, pais?: string | null): string {
  let ciudadLimpia = limpiarCiudadPrefijoPais(ciudad);
  const paisCodigo = normalizarPaisCodigo(pais);
  // Se muestra el NOMBRE del país ("México") en vez del código crudo ("MX") —
  // el código solo queda como fallback para países fuera del catálogo.
  const paisNombre = paisCodigo
    ? PAISES.find((p) => p.code === paisCodigo)?.nombre ?? paisCodigo
    : '';
  // La ciudad no aporta ubicación si es solo un código de país ("mx") o si
  // repite el nombre del país ("mexico" bajo "México"): en ambos casos el
  // dato viene sucio del scraping y se muestra solo el país normalizado.
  if (/^[A-Za-z]{2}$/.test(ciudadLimpia)) ciudadLimpia = '';
  if (ciudadLimpia && paisNombre && sinAcentos(ciudadLimpia) === sinAcentos(paisNombre)) {
    ciudadLimpia = '';
  }
  const partes = [ciudadLimpia, paisNombre].filter((p) => p.length > 0);
  if (partes.length === 0) return 'Ubicación no especificada';
  const resolvedFlag = paisFlag(paisCodigo);
  const flag = resolvedFlag ? `${resolvedFlag} ` : '';
  return flag + partes.join(', ');
}

/**
 * Normaliza el valor crudo de país del scraping a su código ISO del catálogo
 * ("MX"), aceptando códigos ("mx"), nombres ("México", "Mexico") y valores
 * sucios con el código incrustado ("mx México", "México, mx"). Si el valor no
 * se puede resolver contra el catálogo se devuelve recortado tal cual — nunca
 * se inventa un país.
 */
function normalizarPaisCodigo(pais?: string | null): string {
  if (!pais) return '';
  const valor = pais.trim();
  if (!valor) return '';

  const directo = PAISES.find((p) => p.code === valor.toUpperCase());
  if (directo) return directo.code;

  const porNombre = PAISES.find((p) => sinAcentos(p.nombre) === sinAcentos(valor));
  if (porNombre) return porNombre.code;

  // Código de 2 letras incrustado como palabra ("mx México", "México, mx").
  // La frontera de palabra evita falsos positivos dentro de nombres
  // ("Nicaragua" no contiene "ar" como palabra).
  for (const palabra of valor.match(/\b[A-Za-z]{2}\b/g) ?? []) {
    const codigo = PAISES.find((p) => p.code === palabra.toUpperCase());
    if (codigo) return codigo.code;
  }
  return valor;
}

/** Compara nombres ignorando mayúsculas y acentos ("Mexico" ≡ "México"). */
function sinAcentos(valor: string): string {
  return valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function paisSlug(pais?: string | null): string {
  if (!pais) return 'internacional';
  const codigo = normalizarPaisCodigo(pais);
  return PAIS_SLUG_MAP[codigo] ?? codigo.toLowerCase();
}
